import { pdfjsLib } from './pdfWorker';

const DEFAULT_MAX_DIMENSION = 400;
const DEFAULT_QUALITY = 0.8;

/**
 * Render page 1 of a PDF into a JPEG Blob suitable for cloud-thumbnail use.
 * Returns null if rendering fails (encrypted PDFs, corrupted bytes, worker
 * errors) — callers should treat thumbnail generation as best-effort and
 * fall through to the placeholder icon.
 *
 * @param {ArrayBuffer|Uint8Array|Blob|File} source
 * @param {object} [opts]
 * @param {number} [opts.maxDimension=400] — longest side in CSS pixels
 * @param {number} [opts.quality=0.8]
 */
export async function renderPdfThumbnail(source, opts = {}) {
  const maxDimension = opts.maxDimension || DEFAULT_MAX_DIMENSION;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  let data;
  if (source instanceof ArrayBuffer) {
    data = source.slice(0);
  } else if (source instanceof Uint8Array) {
    data = source.slice().buffer;
  } else if (source && typeof source.arrayBuffer === 'function') {
    data = await source.arrayBuffer();
  } else {
    return null;
  }

  let doc = null;
  try {
    doc = await pdfjsLib.getDocument({ data, disableAutoFetch: true, disableStream: true }).promise;
    const page = await doc.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = maxDimension / Math.max(baseViewport.width, baseViewport.height);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    return await new Promise((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob || null),
        'image/jpeg',
        quality
      );
    });
  } catch (err) {
    console.warn('renderPdfThumbnail failed:', err?.message || err);
    return null;
  } finally {
    if (doc) {
      try { await doc.destroy(); } catch { /* best-effort */ }
    }
  }
}
